"use client";

/**
 * The home page: its shell, its bands and WorkingPage, which assembles them. The board's
 * landing study is concept/groups/landing-pages.tsx.
 */

import { type CSSProperties, Fragment, type ReactNode, useId } from "react";

/**
 * LND — THE landing page concept, one version, developed in place. The
 * working page won the four-way comparison on 2026-08-25; since 2026-10-01
 * the home draws it in the application's colours and claret theme, with the
 * site's own type (components/editorial/one-system, set by the page's
 * shell).
 *
 * Composition rules learned in review: the proven full-surface exhibits
 * render in plate mode (chrome off) so the page's own band voice does the
 * talking; product shots stay light; the night ink is reserved for the claret
 * close; everything reads inside a 78rem rail.
 * The current composition runs what a Twin is, how it gets there, what it
 * gives, where it runs, how to start (Archie, 2026-10-01): the hero, whose
 * instrument resolves into the answered product; the Twin story with its
 * four steps in view at once (twin-story/story-steps, which replaced
 * MOT-002's scroll story); a Twin proposal accepted, as motion
 * (landing-match, which replaced the Inbox frame); the agreed meaning
 * reused, told by a workflow's sample run (product-shot/workflow-run-shot);
 * a definition agreed once and then asked for in two tools, as motion
 * (landing-definition, a band of its own added on 2026-10-02);
 * the governed answer (SES-001); hosting and trust as open facts, then the
 * case for a UK-hosted Twin and the choice of model (landing-uk-story,
 * landing-model-choice); and how you start: the engineers who work with
 * you (landing-engineering), then PRC-002's rail with the published
 * starting prices. FAQ-001 answers the objections buyers raise
 * first and links /faq for the rest, and the close has one action. The
 * tools a Twin is used from are the story's fourth step; GOV-007's doors
 * card, which said the same in a band of its own, stays on the board.
 *
 * Copy discipline: what the Twin builds — definitions, links, actions — are
 * proposals a person accepts, declines, puts aside for later or undoes
 * (twin proposals, 2026-09-04). Use those verbs; never write "approve".
 *
 * The group is NOT rendered on the board's main scroll — the gallery
 * filters it; /concept/landing is the only place the page renders.
 */
import { EDITORIAL_ACTION_CLS, EDITORIAL_CONTAINER_CLS, FaqSection, LABEL_CLS, WAITLIST_HREF } from "@/components/editorial";
import { FAQS } from "@/lib/faqs";
import { CtaArrow } from "@/components/editorial/cta-arrow";
import { LogoStrip, WorkingPageHero } from "@/components/editorial/landing-hero";
import { UkMark } from "@/components/editorial/marks";
import { HOW_IT_WORKS_ID, WHY_A_TWIN_ID } from "@/components/editorial/site-links";
import frame from "@/components/editorial/shot-frame.module.css";
import { Circled, SketchCallout, Underlined } from "@/components/sketch";
import { HOME_COPY } from "@/lib/landing-copy";
import { STARTING_PRICES } from "@/app/pricing/pricing-data";
import { StartingPrices } from "@/app/pricing/starting-prices";
import { STORY_STEPS_HEAD, STORY_STEPS_LEDE, StorySteps } from "../twin-story/story-steps";
import { DEFINITION_SUMMARY } from "./definition-story";
import { DefinitionEvidence, DefinitionPoints } from "./landing-definition";
import { EngineeringBand } from "./landing-engineering";
import { HostingInfrastructure } from "./landing-infrastructure";
import { MATCH_SUMMARY, MatchEvidence } from "./landing-match";
import { ModelChoice } from "./landing-model-choice";
import { UkStory } from "./landing-uk-story";
import { ClosingCtaBand } from "./cta";
import { BandRail } from "./pages";
import { ActivationRail } from "./process";
import { AnsweredFrame } from "@/components/product-shot/answered-frame";
import { ReceivablesShot } from "@/components/product-shot/receivables-shot";
import { WorkflowRunShot } from "@/components/product-shot/workflow-run-shot";

const INK_58 = "text-[color-mix(in_srgb,var(--theme-ink)_58%,transparent)]";
const INK_80 = "text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]";

/**
 * The questions the home answers: the objections buyers raise first. The
 * data and cost answers moved into their bands, and /faq keeps every
 * question (the 2026-09-30 copy review).
 */
const HOME_QUESTIONS = [
  "Do we need to migrate our legacy systems first?",
  "Why not just use ChatGPT or Copilot?",
  "We're regulated. Can we actually use this?",
  "What happens when the Twin cannot support a question?",
] as const;

const HOME_FAQS = HOME_QUESTIONS.map((question) => {
  const item = FAQS.find((entry) => entry.q === question);
  if (!item) throw new Error(`The FAQ no longer asks "${question}".`);
  return item;
});

/* ================================================================= */
/*  Page furniture                                                    */
/* ================================================================= */

function PageShell({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <div className={EDITORIAL_CONTAINER_CLS}>
      {/* One reading column. The page lives inside a 78rem rail — the board
          sheet's proportion — so exhibits stay composed on wide viewports
          instead of stretching to the window. */}
      <div className="mx-auto w-full max-w-[78rem]">
        {label ? <p className={`${INK_58} ${LABEL_CLS} pt-10`}>{label}</p> : null}
        {children}
      </div>
    </div>
  );
}

/** A head's sentences, each kept whole: a head that runs to two lines
 *  breaks between its sentences rather than inside one. */
function sentences(head: string): ReactNode {
  return head.split(/(?<=[.?!])\s+/).map((sentence, index) => (
    <Fragment key={sentence}>
      {index > 0 ? " " : null}
      <span className="inline-block">{sentence}</span>
    </Fragment>
  ));
}

/** Stacked band: rail header, then the exhibit at full width. An optional
 *  hand-drawn note sits beside the header from 1024px. */
function Band({
  head,
  lede,
  note,
  children,
  id,
}: {
  head: ReactNode;
  lede: string;
  note?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  const rail = (
    <BandRail head={typeof head === "string" ? sentences(head) : head} headingLevel="h2">
      {lede}
    </BandRail>
  );
  return (
    <section id={id} className="scroll-mt-24 border-b border-dotted border-[var(--rule)] py-12">
      {note ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,38rem)_minmax(0,1fr)] lg:items-end">
          {rail}
          <div className="hidden justify-self-center lg:block">{note}</div>
        </div>
      ) : (
        rail
      )}
      <div className="mt-7 min-w-0">{children}</div>
    </section>
  );
}

/** The UK-hosted Twin's enquiry form, on the research page. */
const UK_ENQUIRY_HREF = "/research#enquiry";

/**
 * The hero's actions (Archie, 2026-10-01), in place of the site's pair: two
 * buttons, the per-user plan at its price and a UK-hosted Twin, and under
 * them the conversation the pair opened, as a link, so the hero still opens
 * the enquiry form without a third button.
 */
function HeroActions() {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <a href="/pricing" className={`${EDITORIAL_ACTION_CLS} bg-[var(--theme-dark)] whitespace-nowrap text-[var(--theme-ground)]`}>
          From {STARTING_PRICES.team} a user a month
        </a>
        <a href={UK_ENQUIRY_HREF} className={`${EDITORIAL_ACTION_CLS} gap-2 border border-[var(--rule)] whitespace-nowrap ${INK_80}`}>
          <UkMark /> Enquire about UK hosting
        </a>
      </div>
      <p className="mt-4 text-[0.9rem] font-semibold">
        <a href={WAITLIST_HREF} data-cta-placement="hero" className={`inline-flex min-h-11 items-center gap-1.5 underline underline-offset-4 ${INK_80}`}>
          Discuss your first workflow <CtaArrow />
        </a>
      </p>
    </div>
  );
}

/**
 * What the hero's instrument resolves into: the sample workspace's decisions
 * question answered, leaning back towards the headline and running off the
 * page's right edge. Flat, and inside the column, on a phone.
 */
function HeroProduct() {
  return (
    <div className={`${frame.tiltLeft} lg:-mr-[40%]`}>
      <AnsweredFrame heightClass="h-[480px] sm:h-[560px]" />
    </div>
  );
}

/**
 * "An answer you can check.", with the verb circled and a pointer from the
 * header into the governed answer (SKT-003, shipped 2026-09-25).
 */
export function CheckableAnswerBand() {
  return (
    <Band
      head={
        <>
          An answer you can <Circled className="mx-[0.22em]">check</Circled>.
        </>
      }
      lede="See who owes what, using the same definition of outstanding debt. Inspect the invoices, ageing calculation and sources behind the answer."
      note={<SketchCallout note={"the sources are\nright here"} arrow="down-left" />}
    >
      <ReceivablesShot />
    </Band>
  );
}

/**
 * The Twin's proposal and a person's call on it, as motion: a customer that
 * Xero and HubSpot name differently, the evidence drawn between the two
 * records, the confidence, Accept, and one linked customer (landing-match;
 * Archie chose it over the Inbox frame on 2026-10-01, and the head and lede
 * on 2026-09-30). The motion is decoration to assistive technology, which
 * reads its summary instead.
 */
export function ProposalReviewBand({ id }: { id?: string }) {
  return (
    <Band
      id={id}
      head="Your Twin finds the match. You make the call."
      lede="When records in two systems look like the same customer, your Twin brings the match to your Inbox with the evidence side by side. Accept it, decline it or leave it for later. An accepted match can be undone."
    >
      <div className="py-6 sm:py-10">
        <MatchEvidence />
      </div>
      <p className="sr-only">{MATCH_SUMMARY}</p>
    </Band>
  );
}

/**
 * The core of a Twin, as a band of its own (Archie, 2026-10-02: "a section
 * for the core of our twin architecture and its value prop", added to the
 * page and not in place of anything): what counts as an overdue invoice,
 * proposed from an invoice's fields, accepted, and then used by two answers
 * asked in different words in different tools (landing-definition). Under
 * the motion, the same three moments as plain points. The head and lede are
 * a draft for his review. The motion is decoration to assistive technology,
 * which reads its summary.
 */
export function DefinitionBand() {
  return (
    <Band
      head="Define it once. Ask it anywhere."
      lede="Ask three people what is overdue and you may get three numbers. Your Twin agrees each definition with you once, then uses it for checked answers wherever your team asks."
    >
      <div className="py-6 sm:py-10">
        <DefinitionEvidence />
      </div>
      <p className="sr-only">{DEFINITION_SUMMARY}</p>
      <DefinitionPoints />
    </Band>
  );
}

/**
 * How you start, with what it costs (the 2026-09-30 copy review): the
 * activation's two weeks at band size, each phase with its time box, then
 * the published starting prices and the pricing page. The close below
 * carries the one action.
 */
export function StartBand() {
  const railId = useId();
  return (
    <Band
      id="start"
      head="We connect the systems. You help define the result."
      lede="Start with one important workflow. In two focused weeks, we connect its systems and check the results with your team."
    >
      <ActivationRail id={railId} timing />
      <div className="mt-10 border-t border-dotted border-[var(--rule)] pt-5">
        <p className={`max-w-[64ch] text-[0.98rem] leading-[1.6] ${INK_80}`}>
          <StartingPrices />
        </p>
      </div>
    </Band>
  );
}

const CLOSING_MARK = "keeps chasing";

/**
 * The dark close with "keeps chasing" underlined in the light accent, so the
 * pen reads on the night surface (SKT-006, shipped 2026-09-25), and one
 * action since the 2026-09-30 copy review. The words stay in HOME_COPY; the
 * mark fails loudly if they change.
 */
export function ClosingBand() {
  const [before, after] = HOME_COPY.closing.split(CLOSING_MARK);
  if (after === undefined) {
    throw new Error(`HOME_COPY.closing no longer contains "${CLOSING_MARK}"; update ClosingBand's mark.`);
  }
  return (
    <section className="my-12" style={{ "--sketch-ink-on": "var(--theme-accent)" } as CSSProperties}>
      <ClosingCtaBand
        title={
          <>
            {before}
            <span className="whitespace-nowrap">
              <Underlined>{CLOSING_MARK}</Underlined>
              {after}
            </span>
          </>
        }
      />
    </section>
  );
}

/** Band for an exhibit that carries its own heading. */
function ExhibitBand({
  children,
  id,
}: {
  children: ReactNode;
  id?: string;
}) {
  return (
    <section id={id} className="scroll-mt-24 border-b border-dotted border-[var(--rule)] py-12">
      <div className="min-w-0">{children}</div>
    </section>
  );
}

/* ================================================================= */
/*  The working page                                                  */
/* ================================================================= */

/** A product shot staged in the page by the viewer's ?shot= option: a whole
 *  hero with the shot as its stage, or a band straight after the client
 *  roster. The home stages none: its hero resolves into the product. */
export type WorkingPageShot = { slot: "hero" | "band"; node: ReactNode };

export function WorkingPage({
  production = false,
  shot,
}: { production?: boolean; shot?: WorkingPageShot } = {}) {
  return (
    <>
      {/* The hero's product runs off the page's right edge; the clip stops
          it widening the page. */}
      <div className="overflow-x-clip">
        <PageShell label={production ? undefined : "MLX · the landing page, working concept"}>
          {shot?.slot === "hero" ? (
            shot.node
          ) : (
            <WorkingPageHero production={production} product={<HeroProduct />} actions={<HeroActions />} />
          )}
        </PageShell>
      </div>
      <PageShell>
        <LogoStrip />
        {shot?.slot === "band" ? <ExhibitBand id="product">{shot.node}</ExhibitBand> : null}

        {/* The order (2026-10-01): what a Twin is, how it gets there, the
            answer it gives, then where it runs, then how to start. */}

        {/* What it is: the Twin story, every step in view at once
            (twin-story/story-steps); the footer's "Why a Twin" lands here. */}
        <Band id={WHY_A_TWIN_ID} head={STORY_STEPS_HEAD} lede={STORY_STEPS_LEDE}>
          <StorySteps />
        </Band>

        {/* How it gets there: the Twin proposes a match and a person decides,
            then the agreed matches and definitions are reused. "How it
            works", in the nav and in the footer, lands on the first of the
            two (Archie, 2026-10-01: one label, one section). */}
        <ProposalReviewBand id={HOW_IT_WORKS_ID} />

        <Band
          head="Agree the meaning. Reuse the understanding."
          lede="Which records describe the same customer? What counts as overdue? Your Twin connects identities, relationships and definitions, then checks the calculations against your data."
        >
          {/* A workflow reusing the agreed matches and ageing, run as a sample
              in the product. PRC-009's authoring chain stays on the board. */}
          <WorkflowRunShot />
        </Band>

        {/* The core, shown: one definition agreed, then the same figure
            wherever it is asked for. It sits between the workflow's results
            and the answer, which are much the same table, and its figure is
            the answer's own "Overdue" line. */}
        <DefinitionBand />

        {/* What it gives: the answer, with its sources. The tools it can be
            asked from are the story's fourth step; the band that repeated
            them (GOV-007's doors card) left on 2026-10-01. */}
        <CheckableAnswerBand />

        {/* Where it runs: what is available today, then the UK-hosted Twin
            in development and the choice of model, which reads on from it. */}
        <Band
          id="infrastructure"
          head="Your Twin. Your infrastructure."
          lede="A dedicated server for your business on Core, Scale and Enterprise, with control over where it runs. Team runs on a shared deployment."
        >
          <HostingInfrastructure />
        </Band>

        <UkStory />

        <ModelChoice />

        {/* How to start: who works with you (forward-deployed engineering,
            a band of its own since 2026-10-01), then the two weeks and the
            prices. */}
        <EngineeringBand />

        <StartBand />

        <FaqSection
          contained
          title="The questions we hear first."
          items={HOME_FAQS}
          more={{ href: "/faq", label: "More questions, including data and cost" }}
        />

        {/* CTA-006's dual band as the footer hero: activation and
            partnership, on the editorial night. */}
        <ClosingBand />

        {production ? null : (
        <p className={`${INK_58} pb-6 text-[0.72rem] leading-[1.5]`}>
          LND-001, the working page. The hero runs the live WebGL twin once,
          with its arrival layer, over the connector grid, and resolves into
          the answered product; then the Twin story in four steps, the match
          as motion, a workflow&rsquo;s sample run, a definition agreed and
          reused, SES-001&rsquo;s governed
          answer, the hosting facts, the UK story and the choice of model,
          and PRC-002&rsquo;s activation timeline; FAQ-001 answers buyer
          questions inline and CTA-006&rsquo;s band closes the page.
        </p>
        )}
      </PageShell>
    </>
  );
}
